import {
    pxArrayToRem,
    useAppDisplayModeContext,
    useStyles,
    Caption,
} from '@archipro-design/aria';
import Specifications from './Specifications';
import RefundPolicy from '@modules/product/component/refund-policy/RefundPolicy';
import ProductCodes from '@modules/product/component/product-codes/ProductCodes';
import * as S from './ProductDetails.style';
import Description from '@modules/product/component/product-description/Description';
import DownloadsAccordion from '@modules/product/component/product-details/DownloadsAccordion';
import { useVariantSelectionState } from '../../context/VariantSelectionContext';
import { useEffect } from 'react';
import { useTracker } from '@archipro-website/tracker';
import useVariantPrice from '../../hook/use-variant-price';
import VariantPOAMobile from '../variant-pricing/VariantPOA.mobile';
import { getEcomEventItemList } from '~/modules/tracking/util/gtmHelper';
import type { ProductPageLoaderData } from '~/routes/_app.product.$urlSegment';
import { AwardAccordion } from './AwardAccordion';
import { useFavouriteVariant } from '../../hook/use-favourite-variant';
import { useVariantStock } from '../../hook/use-variant-stock';
import CompanyProfile from '~/modules/company-profile/CompanyProfile';

export interface ProductCategory {
    PrettyTitle: string;
    Link: string;
    Parents?: ProductCategory[];
}

interface ProductDetailsProps {
    data: ProductPageLoaderData['product'] & {
        primaryCTA: ProductPageLoaderData['primaryCTA'];
        primaryCTASectionRef: React.RefObject<HTMLDivElement>;
        enquireSectionRef: React.RefObject<HTMLDivElement>;
        cartRegion?: {
            ID: number;
            Title: string;
        } | null;
    };
    whereToBuyEnabled?: boolean;
    onClickWhereToBuy?: () => void;
}

const ProductDetails = ({
    data,
    whereToBuyEnabled,
    onClickWhereToBuy,
}: ProductDetailsProps) => {
    const { css } = useStyles();
    const { mobile } = useAppDisplayModeContext();
    const { selectedVariant } = useVariantSelectionState();
    const tracker = useTracker();
    const { hasPrice } = useVariantPrice(data.Pricing.PricingType);
    const productID = data.ID;
    const productTitle = data.Title;
    const variantID = selectedVariant?.ID;
    const priceData = selectedVariant?.Price;
    const price = priceData?.Amount
        ? (priceData?.Amount / 100.0).toFixed(2)
        : 0;
    const currency = priceData?.Currency ?? 'NZD';
    const favouriteItem = useFavouriteVariant(productID);
    const canPurchase = data.CanPurchase;
    const { canAddToCart } = useVariantStock();

    useEffect(() => {
        tracker.log('ViewProduct', {
            url: new URL(window.location.href),

            data: {
                content_ids: productID,
                variant_id: variantID,
                content_type: 'product',
                value: price,
                currency,
            },
        });
    }, [productID, variantID, currency, tracker, price]);

    useEffect(() => {
        if (canPurchase) {
            // ecom remarking event
            tracker.log('view_item', {
                targetTracker: 'googleTagManagerTracker',
                url: new URL(window.location.href),
                gtmData: getEcomEventItemList(
                    [
                        {
                            item_id: productID,
                            item_name: productTitle,
                            item_variant: variantID,
                            index: 0,
                            price: price,
                            quantity: 1,
                        },
                    ],
                    currency
                ),
            });
        }
    }, [
        productID,
        productTitle,
        variantID,
        currency,
        tracker,
        price,
        canPurchase,
    ]);

    const validLibraryDownloads = data.LibraryDownloads.filter(
        (d) => d.InternalLink || d.ExternalLink
    );

    const hasProductCodes = selectedVariant?.SKU || selectedVariant?.Barcode;
    const shouldShowPurchase = data?.EcommerceReady;

    return (
        <>
            <div className="md:pt-6">
                {!data.IsInServiceableArea && (
                    <div className={css(S.Availability)}>
                        <Caption variant={mobile ? '03' : '02'} as="div">
                            Not available in{' '}
                            {data.cartRegion?.Title || 'your location.'}
                        </Caption>
                    </div>
                )}
                <Description
                    data={{
                        Content: data.Content,
                    }}
                    componentAfter={<Specifications data={data} />}
                ></Description>
                {mobile && !hasPrice && (
                    <div ref={data.primaryCTASectionRef}>
                        <VariantPOAMobile
                            professional={{
                                title: data.Professional.Title,
                                id: data.Professional.ID,
                            }}
                            itemID={data.ID}
                            primaryCTA={data.primaryCTA}
                            isInServiceableArea={data.IsInServiceableArea}
                            whereToBuyEnabled={whereToBuyEnabled}
                            onClickWhereToBuy={onClickWhereToBuy}
                            product={data}
                        />
                    </div>
                )}
                <div style={{ padding: mobile ? pxArrayToRem([0, 18]) : 0 }}>
                    {validLibraryDownloads.length ? (
                        <DownloadsAccordion data={data} />
                    ) : null}

                    {data.Awards.length ? (
                        <AwardAccordion awards={data.Awards} />
                    ) : null}

                    {hasProductCodes || data.CanPurchase ? (
                        <div className={css(S.MinorDetail)}>
                            {hasProductCodes ? (
                                <ProductCodes
                                    sku={selectedVariant?.SKU}
                                    barcode={selectedVariant?.Barcode}
                                />
                            ) : (
                                <span />
                            )}
                            {data.CanPurchase ? (
                                <RefundPolicy data={data} />
                            ) : (
                                <span />
                            )}
                        </div>
                    ) : null}
                    {data.IsInServiceableArea && (
                        <div ref={data.enquireSectionRef}>
                            <CompanyProfile
                                showEnquiryForm={true}
                                data={{
                                    Professional: {
                                        ...data.Professional,
                                        Website: data.Professional.Website,
                                        ArchiproEditorialAccount: false,
                                        primaryCTA: data.primaryCTA,
                                    },
                                    siteTreeID: productID,
                                    website: data.Website,
                                    primaryCTA: data.primaryCTA,
                                    itemType: 'product',
                                }}
                                showCTABottom={true}
                                // save to design board button will show beside the add to cart button if the product can be added to the cart
                                favouriteItem={
                                    (shouldShowPurchase && canAddToCart) ||
                                    mobile
                                        ? undefined
                                        : favouriteItem
                                }
                                showWhereToBy={whereToBuyEnabled}
                                onClickWhereToBuy={onClickWhereToBuy}
                            />
                        </div>
                    )}
                </div>
            </div>
        </>
    );
};

export default ProductDetails;
